import type { TokenCredential } from "@azure/identity";
import type { TableEntity, TransactionAction } from "@azure/data-tables";
import {
  applyRequestToEntity,
  blocksNewIntent,
  newIntent,
  stampToEntity,
  entityToStamp,
  type GatewayImageAdoption,
  type ImageStampEntry,
  type InstalledStamp,
} from "@m8t-stack/platform-release";
import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";
import { DEFAULT_IMAGE_REPO, parseContainerAppResourceId, parseImageRef } from "./platform-update.js";
import { readApplyRequest } from "./apply-request-store.js";
import { openMetadataTransactionClient } from "./metadata-table-write.js";

export interface LiveGatewayDeployment {
  name: string;
  tags: Record<string, string>;
  image: string;
  userAssignedIdentityIds: string[];
  registries: { server: string; identity: string; username: string; passwordSecretRef: string }[];
  provisioningState: string;
  latestRevisionName: string;
  latestReadyRevisionName: string;
}

interface ContainerAppWire {
  name?: unknown;
  tags?: unknown;
  identity?: { userAssignedIdentities?: unknown };
  properties?: {
    configuration?: { registries?: unknown };
    template?: { containers?: unknown };
    provisioningState?: unknown;
    latestRevisionName?: unknown;
    latestReadyRevisionName?: unknown;
  };
}

function lowerId(v: string): string {
  return v.replace(/\/$/, "").toLowerCase();
}

function recordOfStrings(v: unknown): Record<string, string> {
  if (!v || typeof v !== "object" || Array.isArray(v)) return {};
  const out: Record<string, string> = {};
  for (const [k, value] of Object.entries(v)) if (typeof value === "string") out[k] = value;
  return out;
}

export function parseLiveGatewayDeployment(raw: unknown): LiveGatewayDeployment {
  const w = (raw ?? {}) as ContainerAppWire;
  const containersRaw = w.properties?.template?.containers;
  const containers = Array.isArray(containersRaw) ? containersRaw : [];
  const gateway = containers.find((c) => (c as { name?: unknown }).name === "gateway") as { image?: unknown } | undefined;
  const uas = w.identity?.userAssignedIdentities;
  const userAssignedIdentityIds = uas && typeof uas === "object" && !Array.isArray(uas) ? Object.keys(uas) : [];
  const registriesRaw = w.properties?.configuration?.registries;
  const regs = Array.isArray(registriesRaw) ? registriesRaw : [];
  return {
    name: typeof w.name === "string" ? w.name : "",
    tags: recordOfStrings(w.tags),
    image: typeof gateway?.image === "string" ? gateway.image : "",
    userAssignedIdentityIds,
    registries: regs.map((r) => {
      const x = (r ?? {}) as Record<string, unknown>;
      return {
        server: typeof x.server === "string" ? x.server : "",
        identity: typeof x.identity === "string" ? x.identity : "",
        username: typeof x.username === "string" ? x.username : "",
        passwordSecretRef: typeof x.passwordSecretRef === "string" ? x.passwordSecretRef : "",
      };
    }),
    provisioningState: typeof w.properties?.provisioningState === "string" ? w.properties.provisioningState : "",
    latestRevisionName: typeof w.properties?.latestRevisionName === "string" ? w.properties.latestRevisionName : "",
    latestReadyRevisionName: typeof w.properties?.latestReadyRevisionName === "string" ? w.properties.latestReadyRevisionName : "",
  };
}

export function legacyGatewayTopologyError(
  live: LiveGatewayDeployment,
  gatewayResourceId: string,
  expectedSubscriptionId?: string,
): string | null {
  const { subscriptionId, resourceGroup, name } = parseContainerAppResourceId(gatewayResourceId);
  if (expectedSubscriptionId && subscriptionId.toLowerCase() !== expectedSubscriptionId.toLowerCase()) return "gateway subscription does not match";
  const prefix = "m8t-gateway-";
  if (!name.startsWith(prefix) || name.length === prefix.length || live.name !== name) return "gateway name is not the generated m8t name";
  const suffix = name.slice(prefix.length);
  if (live.tags.managedBy !== "m8t" || live.tags.m8t !== "gateway") return "gateway ownership tags do not match";

  const parsed = parseImageRef(live.image);
  const parts = parsed.repo.split("/");
  const host = parts.shift() ?? "";
  const repo = parts.join("/");
  if (!host.endsWith(".azurecr.io") || repo !== "m8t-web" || !parsed.tag || !/^v\d{8}-[0-9a-f]{7,40}$/.test(parsed.tag)) {
    return "image is not the legacy m8t ACR image shape";
  }

  const expectedIdentity = `/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.ManagedIdentity/userAssignedIdentities/m8t-acrpull-${suffix}`;
  if (live.userAssignedIdentityIds.length !== 1 || lowerId(live.userAssignedIdentityIds[0]) !== lowerId(expectedIdentity)) {
    return "gateway does not have exactly the generated ACR pull identity";
  }
  if (live.registries.length !== 1) return "gateway does not have exactly one registry credential";
  const registry = live.registries[0];
  if (
    registry.server.toLowerCase() !== host.toLowerCase() ||
    lowerId(registry.identity) !== lowerId(live.userAssignedIdentityIds[0]) ||
    registry.username !== "" ||
    registry.passwordSecretRef !== ""
  ) {
    return "registry credential does not match the generated identity and image host";
  }
  return null;
}

export async function readLiveGatewayDeployment(gatewayResourceId: string): Promise<LiveGatewayDeployment> {
  const { subscriptionId, resourceGroup, name } = parseContainerAppResourceId(gatewayResourceId);
  let raw: string;
  try {
    raw = await runAz(["containerapp", "show", "--subscription", subscriptionId, "-g", resourceGroup, "-n", name, "-o", "json"]);
  } catch (e) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_UNREADABLE", message: `Could not read gateway '${name}'.`, cause: e });
  }
  try {
    return parseLiveGatewayDeployment(JSON.parse(raw));
  } catch (e) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_UNREADABLE", message: `Gateway '${name}' returned invalid JSON.`, cause: e });
  }
}

export async function resolveLegacyAcrDigest(live: LiveGatewayDeployment, subscriptionId?: string): Promise<string> {
  const parsed = parseImageRef(live.image);
  // A compensated legacy ref is tag@digest: the legacy tag preserves the
  // generated-install proof while the embedded digest is the immutable live
  // bytes. Prefer it over re-resolving the mutable tag on an explicit retry.
  if (parsed.digest && /^sha256:[0-9a-f]{64}$/.test(parsed.digest)) return parsed.digest;
  const [host, ...repoParts] = parsed.repo.split("/");
  const acrName = host.split(".")[0];
  const repo = repoParts.join("/");
  if (!acrName || !repo || !parsed.tag) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_INVALID", message: "The live gateway image is not a tagged ACR image." });
  }
  const command = ["acr", "repository", "show"];
  if (subscriptionId) command.push("--subscription", subscriptionId);
  command.push("-n", acrName, "--image", `${repo}:${parsed.tag}`, "--query", "digest", "-o", "tsv");
  const digest = (await runAz(command)).trim();
  if (!/^sha256:[0-9a-f]{64}$/.test(digest)) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_INVALID", message: "The live ACR image did not resolve to a sha256 digest." });
  }
  return digest;
}

function adoptedAcrTopologyError(live: LiveGatewayDeployment, adoption: GatewayImageAdoption): string | null {
  const source = parseImageRef(adoption.expectedSourceRef);
  const current = parseImageRef(live.image);
  const sourceParts = source.repo.split("/");
  const currentParts = current.repo.split("/");
  const expectedAcrName = adoption.expectedAcrResourceId.split("/").filter(Boolean).at(-1) ?? "";
  if (!expectedAcrName || sourceParts[0]?.toLowerCase() !== `${expectedAcrName}.azurecr.io`.toLowerCase()) {
    return "adoption record ACR does not match the authorized image host";
  }
  if (sourceParts[0]?.toLowerCase() !== currentParts[0]?.toLowerCase() || currentParts.slice(1).join("/") !== "m8t-web") {
    return "live image left the explicitly adopted ACR repository";
  }
  if (live.userAssignedIdentityIds.length !== 1 || lowerId(live.userAssignedIdentityIds[0]) !== lowerId(adoption.expectedAcrPullIdentityResourceId)) {
    return "live pull identity differs from the adopted identity";
  }
  if (live.registries.length !== 1) return "live registry credential count differs from the adopted topology";
  const reg = live.registries[0];
  if (
    reg.server.toLowerCase() !== sourceParts[0]?.toLowerCase() ||
    lowerId(reg.identity) !== lowerId(adoption.expectedAcrPullIdentityResourceId) ||
    reg.username !== "" || reg.passwordSecretRef !== ""
  ) {
    return "live registry credential differs from the adopted topology";
  }
  return null;
}

/**
 * Fail closed before an infra deployment can rewrite an explicitly adopted
 * gateway. This is intentionally topology-only: the adoption rail owns image
 * digest transitions, while infra must preserve the authorized ACR repository,
 * pull identity, and registry credential shape it inherited.
 */
export async function assertLiveAdoptedGatewayTopology(args: {
  gatewayResourceId: string;
  adoption: GatewayImageAdoption;
}): Promise<void> {
  const live = await readLiveGatewayDeployment(args.gatewayResourceId);
  const topologyError = adoptedAcrTopologyError(live, args.adoption);
  if (topologyError) {
    throw new LocalCliError({
      code: "PLATFORM_GATEWAY_ADOPTION_DRIFT",
      message: `Adopted gateway held before infra converge: ${topologyError}.`,
    });
  }
}

async function readAcrDigest(subscriptionId: string, acrName: string, image: string): Promise<string | null> {
  try {
    const value = (await runAz(["acr", "repository", "show", "--subscription", subscriptionId, "-n", acrName, "--image", image, "--query", "digest", "-o", "tsv"])).trim();
    return /^sha256:[0-9a-f]{64}$/.test(value) ? value : null;
  } catch {
    return null;
  }
}

async function liveGatewayDigest(
  live: LiveGatewayDeployment,
  subscriptionId: string,
  acrName: string,
): Promise<string | null> {
  const current = parseImageRef(live.image);
  if (current.digest && /^sha256:[0-9a-f]{64}$/.test(current.digest)) return current.digest;
  return current.tag ? readAcrDigest(subscriptionId, acrName, `m8t-web:${current.tag}`) : null;
}

// ACA can keep `latestReadyRevisionName` on the previous revision while the new
// revision is already serving and its startup probes are still within their own
// failure budget. The shipped gateway permits roughly six minutes of startup
// probing, after which ACA still needs time to finalize readiness and traffic.
// Observe immediately, then wait/recheck at most 84 times: seven minutes total,
// bounded well inside the updater lease and request-follow budgets.
const ACA_READINESS_POLL_DELAY_MS = 5_000;
const ACA_READINESS_MAX_SLEEPS = 84;
const ACA_READINESS_OBSERVATIONS = ACA_READINESS_MAX_SLEEPS + 1;

interface GatewayReadinessOptions {
  /** Test-only observation override. Production uses 85 observations. */
  pollAttempts?: number;
  /** Test-only cadence override. Production uses five seconds. */
  pollDelayMs?: number;
  /** Injectable so behavioral tests advance the production cadence without real waits. */
  sleep?: (ms: number) => Promise<void>;
}

function isUnsafeAdoptionDrift(error: unknown): boolean {
  return error instanceof LocalCliError &&
    (error.code === "PLATFORM_GATEWAY_ADOPTION_CHANGED" || error.code === "PLATFORM_GATEWAY_ADOPTION_DRIFT");
}

async function assertRecordedAdoptionState(
  live: LiveGatewayDeployment,
  adoption: GatewayImageAdoption,
  subscriptionId: string,
  acrName: string,
): Promise<string | null> {
  const topologyError = adoptedAcrTopologyError(live, adoption);
  if (topologyError) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_DRIFT", message: `Adopted gateway held while waiting for readiness: ${topologyError}.` });
  }
  const digest = await liveGatewayDigest(live, subscriptionId, acrName);
  if (adoption.status === "pending") {
    const allowed = new Set([
      adoption.expectedSourceDigest,
      adoption.expectedForwardDigest,
      adoption.expectedRollbackDigest,
    ]);
    if (!digest || !allowed.has(digest)) {
      throw new LocalCliError({
        code: "PLATFORM_GATEWAY_ADOPTION_CHANGED",
        message: "The adopted gateway changed to an unrecorded digest while its revision was settling; refusing to overwrite it.",
      });
    }
  }
  return digest;
}

async function waitForAdoptedGatewayReady(args: GatewayReadinessOptions & {
  gatewayResourceId: string;
  adoption: GatewayImageAdoption;
  subscriptionId: string;
  acrName: string;
  expectedImage: string;
  expectedDigest: string;
}): Promise<boolean> {
  const attempts = args.pollAttempts ?? ACA_READINESS_OBSERVATIONS;
  const delayMs = args.pollDelayMs ?? ACA_READINESS_POLL_DELAY_MS;
  const sleep = args.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  for (let n = 0; n < attempts; n += 1) {
    const live = await readLiveGatewayDeployment(args.gatewayResourceId);
    const digest = await assertRecordedAdoptionState(live, args.adoption, args.subscriptionId, args.acrName);
    // ARM exposes terminal provisioning failure on this read shape. Stop the
    // readiness budget immediately so a pending first adoption can compensate;
    // latestReady lag by itself is not terminal and continues to the deadline.
    if (/^(failed|canceled)$/i.test(live.provisioningState)) return false;
    if (
      live.image === args.expectedImage && digest === args.expectedDigest &&
      live.provisioningState === "Succeeded" && live.latestRevisionName !== "" && live.latestRevisionName === live.latestReadyRevisionName
    ) return true;
    if (n + 1 < attempts) await sleep(delayMs);
  }
  return false;
}

/**
 * Compensate the one ownership transition that a same-platform converge cannot
 * roll back by release version. Only the two operator-authorized live states
 * are accepted: the exact source digest or the exact first forward digest.
 * Anything else is drift and is held without overwrite.
 *
 * The restored ref combines the original legacy tag with the recorded digest.
 * That preserves the generated-install proof and audit-friendly tag while
 * making the bytes immutable even if the ACR tag moves after authorization.
 */
export async function restorePendingAdoptedGateway(args: {
  gatewayResourceId: string;
  gateway: ImageStampEntry;
} & GatewayReadinessOptions): Promise<ImageStampEntry> {
  const adoption = args.gateway.adoption;
  if (adoption?.status !== "pending") {
    throw new LocalCliError({
      code: "PLATFORM_GATEWAY_ADOPTION_COMPENSATION_INVALID",
      message: "Gateway adoption compensation requires the original pending authorization.",
    });
  }
  const { subscriptionId, resourceGroup, name } = parseContainerAppResourceId(args.gatewayResourceId);
  const acrName = adoption.expectedAcrResourceId.split("/").filter(Boolean).at(-1) ?? "";
  const source = parseImageRef(adoption.expectedSourceRef);
  const host = source.repo.split("/")[0] ?? "";
  if (!acrName || !host || !source.tag || !/^sha256:[0-9a-f]{64}$/.test(adoption.expectedSourceDigest)) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_COMPENSATION_INVALID", message: "The pending adoption lacks a usable source digest or ACR identity." });
  }

  const live = await readLiveGatewayDeployment(args.gatewayResourceId);
  const topologyError = adoptedAcrTopologyError(live, adoption);
  if (topologyError) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_DRIFT", message: `Gateway adoption held: ${topologyError}.` });
  }
  const currentDigest = await liveGatewayDigest(live, subscriptionId, acrName);
  if (currentDigest !== adoption.expectedSourceDigest && currentDigest !== adoption.expectedForwardDigest) {
    throw new LocalCliError({
      code: "PLATFORM_GATEWAY_ADOPTION_CHANGED",
      message: "The adopted gateway is neither the authorized source nor the authorized first forward digest; refusing compensation.",
    });
  }

  const desired = `${source.repo}:${source.tag}@${adoption.expectedSourceDigest}`;
  if (live.image !== desired) {
    await runAz([
      "containerapp", "update", "--subscription", subscriptionId, "-g", resourceGroup, "-n", name,
      "--container-name", "gateway", "--image", desired,
    ]);
  }

  if (await waitForAdoptedGatewayReady({
    ...args,
    adoption,
    subscriptionId,
    acrName,
    expectedImage: desired,
    expectedDigest: adoption.expectedSourceDigest,
  })) return {
    tag: source.tag,
    digest: adoption.expectedSourceDigest,
    state: "external",
  };
  throw new LocalCliError({
    code: "PLATFORM_GATEWAY_ADOPTION_RESTORE_FAILED",
    message: `Gateway '${name}' did not return ready on the exact authorized source digest.`,
  });
}

export async function rollAdoptedGateway(args: {
  gatewayResourceId: string;
  adoption: GatewayImageAdoption;
  targetTag: string;
  targetDigest: string;
  onProgress?: (message: string) => void;
} & GatewayReadinessOptions): Promise<GatewayImageAdoption> {
  if (!/^sha256:[0-9a-f]{64}$/.test(args.targetDigest)) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_DIGEST_INVALID", message: "The manifest gateway digest is not a lowercase sha256 digest." });
  }
  const { subscriptionId, resourceGroup, name } = parseContainerAppResourceId(args.gatewayResourceId);
  let live = await readLiveGatewayDeployment(args.gatewayResourceId);
  const topologyError = adoptedAcrTopologyError(live, args.adoption);
  if (topologyError) throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_DRIFT", message: `Adopted gateway held: ${topologyError}.` });

  const acrName = args.adoption.expectedAcrResourceId.split("/").filter(Boolean).at(-1) ?? "";
  const host = parseImageRef(args.adoption.expectedSourceRef).repo.split("/")[0];
  if (!acrName || !host) throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_INVALID", message: "The adoption record lacks a usable ACR identity." });

  if (args.adoption.status === "pending") {
    if (
      args.targetDigest !== args.adoption.expectedForwardDigest &&
      args.targetDigest !== args.adoption.expectedRollbackDigest &&
      args.targetDigest !== args.adoption.expectedSourceDigest
    ) {
      throw new LocalCliError({
        code: "PLATFORM_GATEWAY_ADOPTION_TARGET_CHANGED",
        message: "The pending adoption may move only to its recorded forward digest or back to its recorded source digest.",
      });
    }
    const current = parseImageRef(live.image);
    const currentDigest = current.digest ?? (current.tag ? await readAcrDigest(subscriptionId, acrName, `m8t-web:${current.tag}`) : null);
    const atSource = current.repo === `${host}/m8t-web` && currentDigest === args.adoption.expectedSourceDigest;
    const atAuthorizedForward = current.repo === `${host}/m8t-web` && currentDigest === args.adoption.expectedForwardDigest;
    const atTarget = current.repo === `${host}/m8t-web` && currentDigest === args.targetDigest;
    if (!atSource && !atAuthorizedForward && !atTarget) {
      throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_CHANGED", message: "The adopted gateway changed after authorization; refusing to overwrite it." });
    }
  }

  const targetImage = `m8t-web:${args.targetTag}`;
  const existing = await readAcrDigest(subscriptionId, acrName, targetImage);
  if (existing && existing !== args.targetDigest) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_TAG_CONFLICT", message: `ACR tag ${targetImage} already exists with a different digest; refusing to overwrite it.` });
  }
  if (!existing) {
    await runAz([
      "acr", "import", "--subscription", subscriptionId, "-n", acrName,
      "--source", `${DEFAULT_IMAGE_REPO}@${args.targetDigest}`,
      "--image", targetImage,
    ]);
  }
  const imported = await readAcrDigest(subscriptionId, acrName, targetImage);
  if (imported !== args.targetDigest) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_IMPORT_MISMATCH", message: "The mirrored ACR tag does not match the official manifest digest." });
  }

  const waitReady = (image: string, digest: string): Promise<boolean> => waitForAdoptedGatewayReady({
    ...args,
    subscriptionId,
    acrName,
    expectedImage: image,
    expectedDigest: digest,
  });

  const updateTo = async (desired: string): Promise<void> => {
    if (live.image !== desired) {
      await runAz([
        "containerapp", "update", "--subscription", subscriptionId, "-g", resourceGroup, "-n", name,
        "--container-name", "gateway", "--image", desired,
      ]);
    }
  };

  try {
    const targetRef = `${host}/m8t-web@${args.targetDigest}`;
    await updateTo(targetRef);
    if (!(await waitReady(targetRef, args.targetDigest))) {
      throw new LocalCliError({ code: "PLATFORM_GATEWAY_NOT_READY", message: `Gateway '${name}' did not become ready on the manifest digest.` });
    }
  } catch (e) {
    // The first adoption is the only transition from externally-owned bytes to
    // platform-managed bytes. If that transition cannot prove readiness, put the
    // exact operator-authorized source digest back before surfacing failure. A
    // later normal rollback then starts from a known, verified source state.
    if (args.adoption.status === "pending" && !isUnsafeAdoptionDrift(e)) {
      try {
        live = await readLiveGatewayDeployment(args.gatewayResourceId);
        await assertRecordedAdoptionState(live, args.adoption, subscriptionId, acrName);
        const source = parseImageRef(args.adoption.expectedSourceRef);
        const sourceRef = `${source.repo}:${source.tag ?? ""}@${args.adoption.expectedSourceDigest}`;
        if (!source.tag) throw new Error("authorized source does not retain its legacy tag");
        await updateTo(sourceRef);
        if (!(await waitReady(sourceRef, args.adoption.expectedSourceDigest))) throw new Error("source revision did not become ready");
      } catch (restoreError) {
        if (isUnsafeAdoptionDrift(restoreError)) throw restoreError;
        throw new LocalCliError({
          code: "PLATFORM_GATEWAY_ADOPTION_RESTORE_FAILED",
          message: `Gateway adoption failed and restoring the authorized source also failed: ${(restoreError as Error).message}`,
          cause: e,
        });
      }
    }
    throw e;
  }
  args.onProgress?.(`gateway ready at ${args.targetTag} (${args.targetDigest}).`);
  return { ...args.adoption, status: "complete", completedAt: new Date().toISOString() };
}

export function adoptionMatchesLive(adoption: GatewayImageAdoption, live: LiveGatewayDeployment, digest: string): boolean {
  return adoption.expectedSourceRef === live.image && adoption.expectedSourceDigest === digest;
}

function isSamePendingAdoption(
  existing: GatewayImageAdoption | undefined,
  liveImage: string,
  digest: string,
  acrResourceId: string,
  gatewayState: string,
  forwardTag: string,
  forwardDigest: string,
  rollbackTag: string,
  rollbackDigest: string,
): boolean {
  if (!existing) return false;
  return existing.expectedSourceRef === liveImage && existing.expectedSourceDigest === digest &&
    existing.expectedAcrResourceId.toLowerCase() === acrResourceId.toLowerCase() &&
    existing.expectedForwardTag === forwardTag && existing.expectedForwardDigest === forwardDigest &&
    existing.expectedRollbackTag === rollbackTag && existing.expectedRollbackDigest === rollbackDigest &&
    gatewayState === "managed" && existing.status === "pending";
}

export async function adoptLegacyGateway(args: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  gatewayResourceId: string;
  expectedImage: string;
  expectedDigest: string;
  expectedAcrResourceId: string;
  target: string;
  targetGatewayTag: string;
  targetGatewayDigest: string;
  rollbackGatewayTag: string;
  rollbackGatewayDigest: string;
  rollbackPlatformVersion: string;
  operatorIdentity: string;
  nowIso?: string;
  onProgress?: (message: string) => void;
}): Promise<{ outcome: "adopted" | "already-adopted"; image: string; digest: string }> {
  if (!/^sha256:[0-9a-f]{64}$/.test(args.expectedDigest)) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_INVALID", message: "--expected-digest must be a lowercase sha256 digest." });
  }
  const parsedResource = parseContainerAppResourceId(args.gatewayResourceId);
  if (parsedResource.subscriptionId.toLowerCase() !== args.subscriptionId.toLowerCase() || parsedResource.resourceGroup.toLowerCase() !== args.resourceGroup.toLowerCase()) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_SCOPE", message: "The discovered gateway does not belong to the requested subscription and resource group." });
  }
  if (
    !/^sha256:[0-9a-f]{64}$/.test(args.targetGatewayDigest) || args.targetGatewayTag.trim() === "" ||
    !/^sha256:[0-9a-f]{64}$/.test(args.rollbackGatewayDigest) || args.rollbackGatewayTag.trim() === ""
  ) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_INVALID", message: "The selected target lacks a valid gateway tag and digest." });
  }
  const client = await openMetadataTransactionClient({
    credential: args.credential,
    subscriptionId: args.subscriptionId,
    resourceGroup: args.resourceGroup,
    label: "gateway adoption transaction",
    errorCode: "PLATFORM_GATEWAY_ADOPTION_NO_ACCESS",
    retryHint: "The role assignment persists — retry the gateway adoption command in a few minutes.",
    onProgress: args.onProgress,
  });
  const stampRow = await client.getEntity("system", "platform").catch((e: unknown) => {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_NO_STAMP", message: "The installed platform stamp is absent or unreadable; refusing to record gateway ownership.", cause: e });
  });
  const stamp = entityToStamp(stampRow);
  const stampEtag = (stampRow as { etag?: unknown }).etag;
  if (!stamp || typeof stampEtag !== "string" || !stampEtag) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_NO_STAMP", message: "The installed platform stamp is malformed or lacks an ETag." });
  }
  if (stamp.platformVersion !== args.rollbackPlatformVersion) {
    throw new LocalCliError({
      code: "PLATFORM_GATEWAY_ADOPTION_CHANGED",
      message: `The installed platform changed after preflight; expected rollback ${args.rollbackPlatformVersion}, found ${stamp.platformVersion}.`,
    });
  }
  const existingRequest = await readApplyRequest(client);
  const live = await readLiveGatewayDeployment(args.gatewayResourceId);
  const topologyError = legacyGatewayTopologyError(live, args.gatewayResourceId, args.subscriptionId);
  if (topologyError) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_REFUSED", message: `Gateway adoption refused: ${topologyError}.` });
  }
  if (live.image !== args.expectedImage) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_CHANGED", message: `The live gateway image changed; expected '${args.expectedImage}', found '${live.image}'.` });
  }
  const digest = await resolveLegacyAcrDigest(live, args.subscriptionId);
  if (digest !== args.expectedDigest) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_CHANGED", message: `The live gateway digest changed; expected '${args.expectedDigest}', found '${digest}'.` });
  }
  const expectedIdentity = live.userAssignedIdentityIds[0];
  if (args.expectedAcrResourceId.trim() === "" || typeof expectedIdentity !== "string") {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_INVALID", message: "--expected-acr-resource-id and the generated pull identity are required." });
  }
  const imageHost = parseImageRef(live.image).repo.split("/")[0].split(".")[0];
  const acrName = args.expectedAcrResourceId.split("/").filter(Boolean).at(-1) ?? "";
  const expectedAcrId = `/subscriptions/${args.subscriptionId}/resourceGroups/${args.resourceGroup}/providers/Microsoft.ContainerRegistry/registries/${imageHost}`;
  if (imageHost.toLowerCase() !== acrName.toLowerCase() || lowerId(args.expectedAcrResourceId) !== lowerId(expectedAcrId)) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_INVALID", message: "--expected-acr-resource-id does not match the live image registry." });
  }
  const existing = stamp.components.gateway.adoption;
  const samePending = isSamePendingAdoption(
    existing,
    live.image,
    digest,
    args.expectedAcrResourceId,
    stamp.components.gateway.state,
    args.targetGatewayTag,
    args.targetGatewayDigest,
    args.rollbackGatewayTag,
    args.rollbackGatewayDigest,
  );
  if (existing && !samePending) {
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_CONFLICT", message: "The gateway already carries a different adoption record." });
  }
  if (existingRequest && blocksNewIntent(existingRequest.status)) {
    if (samePending && existingRequest.status === "pending" && existingRequest.target === args.target) {
      return { outcome: "already-adopted", image: live.image, digest };
    }
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_IN_PROGRESS", message: `An update request for ${existingRequest.target} is already ${existingRequest.status}.` });
  }
  const now = args.nowIso ?? new Date().toISOString();
  const nextStamp: InstalledStamp = {
    ...stamp,
    updatedAt: now,
    components: {
      ...stamp.components,
      gateway: {
        ...stamp.components.gateway,
        tag: parseImageRef(live.image).tag ?? stamp.components.gateway.tag,
        digest,
        state: "managed",
        adoption: existing ?? {
          version: 1,
          status: "pending",
          expectedSourceRef: live.image,
          expectedSourceDigest: digest,
          expectedForwardDigest: args.targetGatewayDigest,
          expectedForwardTag: args.targetGatewayTag,
          expectedRollbackDigest: args.rollbackGatewayDigest,
          expectedRollbackTag: args.rollbackGatewayTag,
          expectedAcrResourceId: args.expectedAcrResourceId,
          expectedAcrPullIdentityResourceId: expectedIdentity,
          authorizedAt: now,
        },
      },
    },
  };
  const intent = newIntent(args.target, { source: "policy", identity: args.operatorIdentity, at: now }, now);
  const stampWire = stampToEntity(nextStamp);
  const stampEntity: TableEntity = {
    ...stampWire,
    partitionKey: String(stampWire.partitionKey),
    rowKey: String(stampWire.rowKey),
  };
  const applyEntity: TableEntity = applyRequestToEntity(intent);
  const stampAction: TransactionAction = ["update", stampEntity, "Replace", { etag: stampEtag }];
  const requestAction: TransactionAction = existingRequest
    ? ["update", applyEntity, "Replace", { etag: existingRequest.etag }]
    : ["create", applyEntity];
  try {
    await client.submitTransaction([stampAction, requestAction]);
  } catch (e) {
    const status = (e as { statusCode?: number }).statusCode;
    if (status === 409 || status === 412) {
      throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_RACE", message: "Gateway adoption lost a concurrent metadata update; no adoption was recorded.", hint: "Re-read the live image and digest, then retry the command.", cause: e });
    }
    throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_WRITE_FAILED", message: "Could not atomically record gateway adoption and its update request.", cause: e });
  }
  args.onProgress?.(`recorded digest-bound gateway adoption and queued update to ${args.target}.`);
  return { outcome: samePending ? "already-adopted" : "adopted", image: live.image, digest };
}
